import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Eye, ArrowLeftRight } from 'lucide-react';
import { getMyFamily, switchProfile } from '../../redux/familySlice';

// "You are viewing X. You cannot make changes."
//
// Shown whenever the current profile was shared at READ level. Without it the
// app looks completely normal until a button fails, and being refused is the
// worst possible way to learn what your access is.
//
// THIS IS NOT THE CONTROL. The server refuses every write from a read-only
// profile whatever this component does — see api/middleware/requireWriteAccess.
// This exists so people are not surprised, not to keep anyone out.
export default function ReadOnlyBanner() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { primary, members, activePatientId } = useSelector((s) => s.family || {});
    const [leaving, setLeaving] = useState(false);

    useEffect(() => { if (!primary) dispatch(getMyFamily()); }, [primary, dispatch]);

    const people = [primary, ...(members || [])].filter(Boolean);
    const active = people.find((p) => p.patientId === activePatientId);

    // Nothing to say when acting as yourself, or when you hold full rights.
    if (!active || active.permission !== 'read') return null;

    const name = [active.name, active.surname].filter(Boolean).join(' ').trim();

    const backToOwn = async () => {
        if (!primary?.patientId) return;
        setLeaving(true);
        try {
            await dispatch(switchProfile(primary.patientId)).unwrap();
            window.location.reload();
        } catch {
            setLeaving(false);
        }
    };

    return (
        <div
            data-testid="read-only-banner"
            className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-900"
        >
            <Eye className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
                {t('family.readonly_viewing', 'You are viewing')} <b>{name}</b>
                {' — '}
                {t('family.readonly_note', 'read only. You can see the records but not change them.')}
            </span>
            <button
                type="button"
                onClick={backToOwn}
                disabled={leaving}
                className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded-lg border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-900 transition hover:bg-amber-100 disabled:opacity-50"
            >
                <ArrowLeftRight className="h-3 w-3" />
                {t('family.back_to_my_account', 'Back to my account')}
            </button>
        </div>
    );
}
